import axios from 'axios';
import {User} from '../../security/model/user';
import {configSettings} from '../../shared/configuration/config-settings';
import {Client} from '../../shared/model/client';
import {Facility} from '../../shared/model/facility';
import {axiosReadHelper} from '../../shared/service/axios-read-helper';
import {AxiosReadPayload} from '../../shared/service/axios-read-payload';
import {AxiosResultStatus} from '../../shared/service/axios-result-status';
import {axiosSaveHelper} from '../../shared/service/axios-save-helper';
import {AxiosSavePayload} from '../../shared/service/axios-save-payload';
import {UserInfo} from '../models/user-info';
import {UserInfoClient} from '../models/user-info-client';
import {UserInfoFacility} from '../models/user-info-facility';
import {TimingRisk} from '../../meta-data/models/metaData';
import {UserInfoRole} from '../models/user-info-role';

export const adminUserHelper = {

  mapClientForSearch(payload: Client[]): UserInfoClient[] {
    return payload?.map((clientData: Client) => {
      const user = new UserInfoClient();
      user.clientId = clientData.clientId;
      user.clientName = clientData.clientName;
      //isFacilityLevel and allowedFacilities array is out of sync
      //for now, if the allowedFacilities array is empty, we assume the user has access to all facilities for the client
      user.isFacilityLevel = Array.isArray(clientData.allowedFacilities) && clientData.allowedFacilities.length === 0  ? true : clientData.isFacilityLevel
      user.allowedFacilities = [];
      if (Array.isArray(clientData.allowedFacilities) && clientData.allowedFacilities.length > 0) {
        user.allowedFacilities = clientData.allowedFacilities.map((facilityData: Facility) => {
          const facility = new UserInfoFacility();
          facility.facilityId = facilityData.clientFacilityId!;
          facility.facilityName = facilityData.facilityName;
          return facility;
        });
      }
      return user;
    });
  },
  mapUserTimingRiskForSearch(payload: TimingRisk[]): TimingRisk[] {
    if (!payload) {
      return [];
    }

    return payload.map((timingRisk: TimingRisk) => {
      const userTimingRisk = new TimingRisk();
      userTimingRisk.timingRiskId = timingRisk.timingRiskId;
      return userTimingRisk;
    });
  },


  mapUser(userData: User): UserInfo {
    const user = new UserInfo();
    user.userId = userData.userId;
    user.email = userData.userName;
    user.firstName = userData.firstName;
    user.lastName = userData.lastName;
    user.phoneNumber = userData.mobilePhone;
    user.fullName = `${userData.firstName} ${userData.lastName}`;
    user.isActive = userData.isActive;
    user.cognitoStatus = userData.cognitoStatus;
    user.domainAccess = userData?.userScope?.toUpperCase();
    user.isSSO = userData.isSSO;
    user.hasAllTimingRisks = userData.hasAllTimingRisks;
    user.isPatient = userData?.isPatient;

    if (userData.userRole && userData.userRole.userRoleId) {
      user.userRole = {
        roleId: userData.userRole.userRoleId,
        roleName: userData.userRole.roleName,
      }
    }

    user.clients = this.mapClientForSearch(userData.allowedClients)

    user.userTimingRisk = this.mapUserTimingRiskForSearch(userData.allowedTimingRisks
      )

    return user;
  },

  mapUsersForSearch(payload: User | User[]): UserInfo[] {
    // If payload is undefined/null, return empty array
    if (!payload) {
      return [];
    }

    // If payload is a single user, wrap it in an array
    const userArray = Array.isArray(payload) ? payload : [payload];
    
    return userArray.map((userData: User) => {
      return this.mapUser(userData);
    });
  },






}

type GetAllUserFilters = {
  firstName?: string;
  lastName?: string;
  userName?: string;
  role?:  number;
  client?:  number ;
  facility?:  number;
}

export class AdminUserService {
  async getAllUsers(offset: number, limit: number, filters: GetAllUserFilters): Promise<AxiosResultStatus> {

    // converting all values of filters to strings.
    const filtersAsString: Record<string, string> = {};
    for (const key of Object.keys(filters) as (keyof GetAllUserFilters)[]) {
      const value = filters[key];
      if (value !== undefined) {
        filtersAsString[key] = String(value);
      }
    }

    const querystringParams = new URLSearchParams(filtersAsString).toString();

    const queryString = querystringParams ? `${querystringParams}` : '';

    const readPayload: AxiosReadPayload = {
      dataId: 0,
      url: `user/v2/user?${queryString ? `&${queryString}` : ''}`,
    }

    const responseData = await axiosReadHelper(readPayload)
    const allUsers = adminUserHelper.mapUsersForSearch(responseData.entity);

    responseData.entity = allUsers;
    return responseData;
  }

  async getUserById(userId: number): Promise<AxiosResultStatus> {
    const readPayload: AxiosReadPayload = {
      dataId: 0,
      url: `user/v2/user/${userId}`
    }
    return await axiosReadHelper(readPayload);
  }


  async getPaymentChannels() {
    const responseAPI = await axios.get(`${configSettings.unified_aws_app_gateway_uri}/payment/v2/payment/paymentchannels`);
    const paymentChannels = responseAPI.data;
    return paymentChannels;
  }


  async createOrUpdateUser(data: Partial<Omit<UserInfo, 'userRole'>> & { userRole: Partial<UserInfoRole>}): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      dataToSave: data,
      dataId: data.userId ? data.userId : 0,
      url: "user/v2/user",

    }
    return await axiosSaveHelper(payload);
  }

  async resendInvitation(userId: number): Promise<AxiosResultStatus> {
    const payload: AxiosSavePayload = {
      url: `user/v2/user/${userId}/welcome-email`,
    };
    return await axiosSaveHelper(payload);
  }

}

export const userService = new AdminUserService();

